import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { logError } from 'services/logging/logging';
import { IntegrationDTO, RelationshipDocumentStatus, RelationshipStatus } from 'support/types';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';

export type TaskResponsibility = 'current_partner' | 'other_partner' | 'procuros' | null;
type IntegrationState = Pick<IntegrationDTO, 'state'>;

export const RelationshipStateBadge = ({
  status,
  requester,
  sender,
  taskResponsibility = null,
  className,
  children,
  senderIntegration,
  receiverIntegration,
}: {
  status: RelationshipDocumentStatus;
  requester: boolean;
  sender: boolean;
  taskResponsibility?: TaskResponsibility;
  className?: string;
  children?: ReactNode;
  senderIntegration?: IntegrationState | undefined;
  receiverIntegration?: IntegrationState | undefined;
}) => {
  const { t } = useTranslation();
  const label = stateToLabel(t, sender, requester, taskResponsibility, status, senderIntegration, receiverIntegration);

  return (
    <Badge
      color={stateToColor(sender, taskResponsibility, status)}
      hasDot
      tooltip={children ? label : undefined}
      className={className}
    >
      {children ?? label}
    </Badge>
  );
};

const InviteeRequesterStates = [
  RelationshipStatus.PARTNER_APPROVAL_PENDING,
  RelationshipStatus.PARTNER_ONBOARDING_PENDING,
  RelationshipStatus.PARTNER_PROCESS_PENDING,
  RelationshipStatus.ISSUE_RESOLVER_PENDING,
];

const SenderReceiverStates = [
  RelationshipStatus.READY_FOR_TESTING,
  RelationshipStatus.HAS_TEST_MESSAGES,
  RelationshipStatus.IN_REVIEW,
];

const EqualStates = [
  RelationshipStatus.REJECTED,
  RelationshipStatus.BLOCKED,
  RelationshipStatus.LIVE,
  RelationshipStatus.DEACTIVATED,
  RelationshipStatus.ACTIVE,
  RelationshipStatus.GO_LIVE_DATE_SCHEDULED,
  RelationshipStatus.GO_LIVE_DATE_PENDING,
  RelationshipStatus.PARTNER_INTEGRATION_PENDING,
];

/*
 * i18n
 * 
 * Tasks
 * t('connections:relationshipsBadges.TASKS_PENDING.current_partner')
 * t('connections:relationshipsBadges.TASKS_PENDING.other_partner')
 * t('connections:relationshipsBadges.TASKS_PENDING.procuros')
 *
 * Onboarding
 * t('connections:relationshipsBadges.PARTNER_APPROVAL_PENDING.invitee')
 * t('connections:relationshipsBadges.PARTNER_APPROVAL_PENDING.requester')
 * t('connections:relationshipsBadges.PARTNER_ONBOARDING_PENDING.invitee')
 * t('connections:relationshipsBadges.PARTNER_ONBOARDING_PENDING.requester')
 * t('connections:relationshipsBadges.PARTNER_PROCESS_PENDING.invitee')
 * t('connections:relationshipsBadges.PARTNER_PROCESS_PENDING.requester')
 * t('connections:relationshipsBadges.ISSUE_RESOLVER_PENDING.invitee')
 * t('connections:relationshipsBadges.ISSUE_RESOLVER_PENDING.requester')

 *
 * t('connections:relationshipsBadges.PARTNER_INTEGRATION_PENDING.missingIntegration')
 * t('connections:relationshipsBadges.PARTNER_INTEGRATION_PENDING.missingPartnerIntegration')
 *
 *
 * In testing
 * t('connections:relationshipsBadges.READY_FOR_TESTING.sender')
 * t('connections:relationshipsBadges.READY_FOR_TESTING.receiver')
 * t('connections:relationshipsBadges.HAS_TEST_MESSAGES.sender')
 * t('connections:relationshipsBadges.HAS_TEST_MESSAGES.receiver')
 * t('connections:relationshipsBadges.IN_REVIEW.sender')
 * t('connections:relationshipsBadges.IN_REVIEW.receiver')
 *
 * Blocked
 * t('connections:relationshipsBadges.BLOCKED')
 * 
 * Live
 * t('connections:relationshipsBadges.LIVE')
 * 
 * Ready
 * t('connections:relationshipsBadges.ACTIVE')
 * t('connections:relationshipsBadges.GO_LIVE_DATE_SCHEDULED')
 * t('connections:relationshipsBadges.GO_LIVE_DATE_PENDING')
 
 *
 * Deactivated
 * t('connections:relationshipsBadges.DEACTIVATED')
 * t('connections:relationshipsBadges.REJECTED')
 * t('connections:relationshipsBadges.REJECTED')
 *
 */
const stateToLabel = (
  t: TFunction,
  sender: boolean,
  requester: boolean,
  taskResponsibility: TaskResponsibility,
  state: RelationshipDocumentStatus,
  senderIntegration: IntegrationState | undefined,
  receiverIntegration: IntegrationState | undefined,
): string | undefined | ReactNode => {
  if (state === RelationshipStatus.TASKS_PENDING) {
    return t(`connections:relationshipsBadges.TASKS_PENDING.${taskResponsibility}`);
  }

  if (state === RelationshipStatus.PARTNER_INTEGRATION_PENDING) {
    return <PartnerIntegrationPending integration={sender ? senderIntegration : receiverIntegration} />;
  }

  if (SenderReceiverStates.includes(RelationshipStatus[state])) {
    return t(`connections:relationshipsBadges.${state}.${sender ? 'sender' : 'receiver'}`);
  } else if (InviteeRequesterStates.includes(RelationshipStatus[state])) {
    return t(`connections:relationshipsBadges.${state}.${requester ? 'requester' : 'invitee'}`);
  } else if (EqualStates.includes(RelationshipStatus[state])) {
    return t(`connections:relationshipsBadges.${state}`);
  }

  logError(`Unknown state translation: ${RelationshipStatus[state]}`);
  return undefined;
};

const stateToColor = (
  sender: boolean,
  taskResponsibility: TaskResponsibility,
  state: RelationshipDocumentStatus,
): BadgeColor | undefined => {
  if (SenderReceiverStates.includes(RelationshipStatus[state])) {
    switch (state) {
      case RelationshipStatus.READY_FOR_TESTING:
      case RelationshipStatus.HAS_TEST_MESSAGES:
        return sender ? BadgeColor.blue : BadgeColor.gray;
      case RelationshipStatus.IN_REVIEW:
        return sender ? BadgeColor.gray : BadgeColor.blue;
    }
  } else if (InviteeRequesterStates.includes(RelationshipStatus[state])) {
    return BadgeColor.gray;
  }

  if (state === RelationshipStatus.TASKS_PENDING) {
    return taskResponsibility === 'current_partner' ? BadgeColor.blue : BadgeColor.gray;
  }

  switch (state) {
    case RelationshipStatus.ACTIVE:
      return BadgeColor.blue;
    case RelationshipStatus.LIVE:
      return BadgeColor.green;
    case RelationshipStatus.DEACTIVATED:
    case RelationshipStatus.BLOCKED:
      return BadgeColor.gray;
  }

  if (
    [
      RelationshipStatus.GO_LIVE_DATE_SCHEDULED,
      RelationshipStatus.GO_LIVE_DATE_PENDING,
      RelationshipStatus.PARTNER_INTEGRATION_PENDING,
    ].includes(RelationshipStatus[state])
  ) {
    return BadgeColor.gray;
  }

  logError(`Unknown state color badge: ${state}`);

  return undefined;
};

const PartnerIntegrationPending = ({ integration }: { integration: IntegrationState | undefined }) => {
  const { t } = useTranslation();
  if (integration?.state !== 'READY') {
    return t('connections:relationshipsBadges.PARTNER_INTEGRATION_PENDING.missingIntegration');
  }
  return t('connections:relationshipsBadges.PARTNER_INTEGRATION_PENDING.missingPartnerIntegration');
};
